import React from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { UniversalLink } from "@plone/volto/components";
import { getUser, logout } from "@plone/volto/actions";
import registry from "@plone/volto/registry";
import cx from "classnames";
import "./authbanner.css";

export default function AuthBanner() {
  const dispatch = useDispatch();

  // config-driven paths with safe fallbacks
  const ab = registry?.settings?.authBanner || {};
  const loginPath = ab.loginPath ?? "/login";
  const registerPath = ab.registerPath ?? "/register";
  const postLogoutPath = ab.postLogoutPath ?? "/";

  // selectors (always called)
  // Pure Redux subscription — reactive to IPLoginHandler's LOGIN_SUCCESS dispatch
  const token = useSelector(
    (s) => s.userSession?.token || s.userSession?.authToken,
  );
  const usersSlice = useSelector((s) => s.users || {});
  const ui = useSelector((s) => s);

  const isAuthenticated = !!token;

  // Read from localStorage — IPLoginHandler sets this on a successful IP login.
  // Also listens for the 'ipAuthDetected' custom event fired by IPLoginHandler
  // Stage 2 (content probe fallback), where no JWT is available but a Plone
  // session cookie is active.
  const [isIpAuth, setIsIpAuth] = React.useState(
    typeof window !== "undefined" &&
      localStorage.getItem("ipAuthLogin") === "true",
  );

  // Cookie-session fallback: no JWT means isAuthenticated stays false,
  // so we track this separately to drive banner state.
  const [isCookieSession, setIsCookieSession] = React.useState(
    typeof window !== "undefined" &&
      localStorage.getItem("ipAuthLogin") === "true" &&
      !token,
  );

  // Listen for the custom event fired by IPLoginHandler Stage 2
  React.useEffect(() => {
    const handleIpAuthDetected = () => {
      console.log("[AuthBanner] IP auth detected via cookie session.");
      setIsIpAuth(true);
      setIsCookieSession(true);
    };
    window.addEventListener("ipAuthDetected", handleIpAuthDetected);
    return () => {
      window.removeEventListener("ipAuthDetected", handleIpAuthDetected);
    };
  }, []);

  // Keep isIpAuth in sync if JWT token arrives after mount (Stage 1 flow)
  React.useEffect(() => {
    if (isAuthenticated && typeof window !== "undefined") {
      setIsIpAuth(localStorage.getItem("ipAuthLogin") === "true");
    }
  }, [isAuthenticated]);

  const user = usersSlice.user || null;

  // anchor setup (always called)
  const [target, setTarget] = React.useState(null);
  React.useEffect(() => {
    if (typeof document === "undefined") return;

    const ensureAnchor = () => {
      let anchor = document.querySelector("#auth-banner-anchor");
      if (anchor) return anchor;
      const header = document.querySelector(".header-wrapper");
      if (!header) return null;
      anchor = document.createElement("div");
      anchor.id = "auth-banner-anchor";
      header.parentNode.insertBefore(anchor, header.nextSibling);
      return anchor;
    };

    let anchor = ensureAnchor();
    if (anchor) setTarget(anchor);

    let obs;
    if (!anchor && typeof MutationObserver !== "undefined") {
      obs = new MutationObserver(() => {
        anchor = ensureAnchor();
        if (anchor) {
          setTarget(anchor);
          obs.disconnect();
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      if (obs) obs.disconnect();
    };
  }, []);

  // fetch user profile once after login (JWT sessions only)
  React.useEffect(() => {
    if (isAuthenticated && !usersSlice.user) {
      dispatch(getUser("@me"));
    }
  }, [isAuthenticated, usersSlice.user, dispatch]);

  // Combined auth state — true for both JWT and cookie sessions
  const isLoggedIn = isAuthenticated || isCookieSession;

  // console log auth state for easy testing
  React.useEffect(() => {
    if (isAuthenticated) {
      const displayName =
        user?.fullname || user?.fullName || user?.username || null;
      console.log(
        `[AuthBanner] Logged in${displayName ? ` as ${displayName}` : " (profile loading)"}${isIpAuth ? " via IP auth (JWT)" : ""}.`,
      );
    } else if (isCookieSession) {
      console.log(
        "[AuthBanner] Logged in via IP auth (cookie session — no JWT).",
      );
    } else {
      console.log("[AuthBanner] Not logged in.");
    }
  }, [isAuthenticated, isCookieSession, isIpAuth, user]);

  // guards as booleans (no early returns)
  const inEdit =
    ui?.toolbar?.showing ||
    ui?.content?.subrequest?.edit ||
    ui?.content?.editing;

  const path =
    (typeof window !== "undefined" &&
      window.location &&
      window.location.pathname) ||
    "";

  const hideForRoute =
    path.startsWith(loginPath) || path.startsWith(registerPath);
  const shouldHide = inEdit || hideForRoute;

  // handlers
  const onLogout = React.useCallback(
    (e) => {
      e.preventDefault();
      localStorage.removeItem("ipAuthLogin");
      sessionStorage.removeItem("ipAuthActive");
      dispatch(logout());
      if (typeof window !== "undefined") {
        window.location.assign(postLogoutPath);
      }
    },
    [dispatch, postLogoutPath],
  );

  // render
  const displayName =
    user?.fullname || user?.fullName || user?.username || null;

  const content = shouldHide ? null : (
    <div
      className={cx("auth-banner", {
        "logged-in": isLoggedIn,
        "ip-logged-in": isLoggedIn && isIpAuth,
      })}
    >
      {isLoggedIn ? (
        <div className="auth-banner__inner">
          <span className="auth-banner__text">
            Welcome. {`Logged in${displayName ? ` as ${displayName}` : ""}.`}{" "}
            <UniversalLink href="/personal-information">
              Access your dashboard
            </UniversalLink>
          </span>
          <div className="auth-banner__actions">
            <button
              type="button"
              className="auth-banner__btn auth-banner__btn--danger"
              onClick={onLogout}
            >
              Log out
            </button>
          </div>
        </div>
      ) : (
        <div className="auth-banner__inner">
          <span className="auth-banner__text">You are not logged in.</span>
          <div className="auth-banner__actions">
            <UniversalLink
              className="auth-banner__btn auth-banner__btn--primary"
              href={registerPath}
            >
              Subscribe
            </UniversalLink>
            <UniversalLink className="auth-banner__btn" href={loginPath}>
              Log in
            </UniversalLink>
          </div>
        </div>
      )}
    </div>
  );

  if (!target)
    return <span style={{ display: "none" }} data-authbanner="mount" />;
  return ReactDOM.createPortal(content, target);
}

/* Add this back to re-add subscribe button
<UniversalLink
  className="auth-banner__btn auth-banner__btn--primary"
  href={registerPath}
>
  Subscribe
</UniversalLink>
*/
//Change welcome message when IP auth is sorted:
//{`Logged in${displayName ? ` as ${displayName}` : ""}.`}
